/* Card de story da peca: desenha um 1080x1920 no canvas e entrega o PNG para a
 * folha de compartilhamento do sistema, onde o Instagram aparece como destino
 * no celular. Onde compartilhar arquivo nao existe (desktop), sobra o download.
 *
 * O PNG e montado quando o modal abre, e nao no clique de compartilhar: o iOS
 * exige que navigator.share() rode dentro da ativacao do gesto do usuario, e
 * carregar imagem + desenhar + toBlob consumiria essa janela. */

const CARD = { w: 1080, h: 1920, pad: 72 };

/* Marcas verticais em px do card final. */
const LAYOUT = {
  logoTop: 72,
  logoHeight: 92,
  photoTop: 210,
  photoHeight: 1080,
  vendorBaseline: 1372,
  titleFirstBaseline: 1470,
  titleLineHeight: 88,
  titleMaxLines: 3,
  titleSizes: [80, 72, 64, 56],
  dividerY: 1706,
  footerBaseline: 1772,
  domainBaseline: 1862,
};

const FOOTER_SIZES = [27, 25, 23, 21, 19];

/* Safari so ganhou ctx.letterSpacing no 17.4; sem ele o desenho vai caractere
 * por caractere para nao perder o entreletras do design. */
const SUPPORTS_LETTER_SPACING = (() => {
  try {
    const probe = document.createElement('canvas').getContext('2d');
    probe.letterSpacing = '4px';
    return probe.letterSpacing === '4px';
  } catch (error) {
    return false;
  }
})();

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`nao carregou: ${src}`));
    image.src = src;
  });
}

/* Pede da CDN a largura que o card precisa, em vez da miniatura da galeria. */
function sizedUrl(src, width) {
  try {
    const url = new URL(src, window.location.href);
    url.searchParams.set('width', String(width));
    return url.href;
  } catch (error) {
    return src;
  }
}

async function ensureFonts(family) {
  if (!document.fonts) return;
  const specs = [`700 80px ${family}`, `600 34px ${family}`, `500 28px ${family}`];
  try {
    await Promise.all(specs.map((spec) => document.fonts.load(spec)));
  } catch (error) {
    /* segue com a fonte de sistema */
  }
}

/* Largura visual do texto ja com o entreletras: medir sem ele deixava o texto
 * alinhado a direita transbordar a margem. O ultimo vao entra na conta do
 * measureText nativo e por isso e descontado. */
function measureTracked(ctx, text, spacing) {
  if (SUPPORTS_LETTER_SPACING) {
    if (!text) return 0;
    const previous = ctx.letterSpacing;
    ctx.letterSpacing = `${spacing}px`;
    const width = ctx.measureText(text).width;
    ctx.letterSpacing = previous;
    return width - spacing;
  }
  const chars = Array.from(text);
  if (!chars.length) return 0;
  let width = 0;
  chars.forEach((char) => {
    width += ctx.measureText(char).width + spacing;
  });
  return width - spacing;
}

function drawTracked(ctx, text, x, y, spacing) {
  ctx.textAlign = 'left';
  if (SUPPORTS_LETTER_SPACING) {
    ctx.letterSpacing = `${spacing}px`;
    ctx.fillText(text, x, y);
    ctx.letterSpacing = '0px';
    return;
  }
  let cursor = x;
  Array.from(text).forEach((char) => {
    ctx.fillText(char, cursor, y);
    cursor += ctx.measureText(char).width + spacing;
  });
}

function drawCover(ctx, image, x, y, width, height) {
  const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
  const drawWidth = image.naturalWidth * scale;
  const drawHeight = image.naturalHeight * scale;
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, width, height);
  ctx.clip();
  ctx.drawImage(image, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight);
  ctx.restore();
}

function wrapLines(ctx, text, maxWidth) {
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let current = '';
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (!current || ctx.measureText(candidate).width <= maxWidth) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  });
  if (current) lines.push(current);
  return lines;
}

/* Desce o corpo do titulo ate caber nas linhas disponiveis. */
function fitTitle(ctx, text, maxWidth, family) {
  let chosen = { size: LAYOUT.titleSizes[LAYOUT.titleSizes.length - 1], lines: [] };
  for (let i = 0; i < LAYOUT.titleSizes.length; i += 1) {
    const size = LAYOUT.titleSizes[i];
    ctx.font = `700 ${size}px ${family}`;
    const lines = wrapLines(ctx, text, maxWidth);
    chosen = { size, lines };
    if (lines.length <= LAYOUT.titleMaxLines) return chosen;
  }
  return { size: chosen.size, lines: chosen.lines.slice(0, LAYOUT.titleMaxLines) };
}

/* Condicao/tipo a esquerda e selo a direita dividem uma linha so. Com textos
 * longos os dois se encostavam, entao o corpo desce ate sobrar respiro no meio. */
function fitFooter(ctx, data, spacing) {
  const maxWidth = CARD.w - CARD.pad * 2;
  const minGap = 56;
  let chosen = FOOTER_SIZES[FOOTER_SIZES.length - 1];
  for (let i = 0; i < FOOTER_SIZES.length; i += 1) {
    const size = FOOTER_SIZES[i];
    ctx.font = `500 ${size}px ${data.font}`;
    const left = data.metaLeft ? measureTracked(ctx, data.metaLeft, spacing) : 0;
    const sealDot = Math.round(size / 4) * 2 + 24;
    const right = data.metaRight ? measureTracked(ctx, data.metaRight, spacing) + sealDot : 0;
    const gap = left && right ? minGap : 0;
    chosen = size;
    if (left + right + gap <= maxWidth) break;
  }
  return chosen;
}

function drawBadge(ctx, text, centerY, data) {
  const spacing = 5;
  ctx.font = `600 32px ${data.font}`;
  const textWidth = measureTracked(ctx, text, spacing);
  const boxHeight = 62;
  const boxWidth = textWidth + 52;
  const x = CARD.w - CARD.pad - boxWidth;
  const y = centerY - boxHeight / 2;
  ctx.strokeStyle = data.accent;
  ctx.lineWidth = 2;
  ctx.strokeRect(x + 1, y + 1, boxWidth - 2, boxHeight - 2);
  ctx.fillStyle = data.accent;
  ctx.textBaseline = 'middle';
  drawTracked(ctx, text, x + 26, centerY + 1, spacing);
  ctx.textBaseline = 'alphabetic';
}

function drawCard(ctx, data, assets) {
  const maxWidth = CARD.w - CARD.pad * 2;

  ctx.clearRect(0, 0, CARD.w, CARD.h);
  ctx.fillStyle = data.background;
  ctx.fillRect(0, 0, CARD.w, CARD.h);
  ctx.textBaseline = 'alphabetic';

  if (assets.logo) {
    const height = LAYOUT.logoHeight;
    const width = Math.round(assets.logo.naturalWidth * (height / assets.logo.naturalHeight));
    ctx.drawImage(assets.logo, CARD.pad, LAYOUT.logoTop, width, height);
  }

  if (data.badge) drawBadge(ctx, data.badge, LAYOUT.logoTop + LAYOUT.logoHeight / 2, data);

  drawCover(ctx, assets.photo, CARD.pad, LAYOUT.photoTop, maxWidth, LAYOUT.photoHeight);

  if (data.vendor) {
    ctx.font = `700 46px ${data.font}`;
    ctx.fillStyle = data.accent;
    drawTracked(ctx, data.vendor, CARD.pad, LAYOUT.vendorBaseline, 6);
  }

  if (data.title) {
    const fitted = fitTitle(ctx, data.title, maxWidth, data.font);
    ctx.fillStyle = data.foreground;
    ctx.font = `700 ${fitted.size}px ${data.font}`;
    ctx.textAlign = 'left';
    fitted.lines.forEach((line, index) => {
      ctx.fillText(line, CARD.pad, LAYOUT.titleFirstBaseline + index * LAYOUT.titleLineHeight);
    });
  }

  ctx.fillStyle = data.rule;
  ctx.fillRect(CARD.pad, LAYOUT.dividerY, maxWidth, 3);

  const footerSpacing = 5;
  const footerSize = fitFooter(ctx, data, footerSpacing);
  ctx.font = `500 ${footerSize}px ${data.font}`;

  if (data.metaLeft) {
    ctx.fillStyle = data.muted;
    drawTracked(ctx, data.metaLeft, CARD.pad, LAYOUT.footerBaseline, footerSpacing);
  }

  if (data.metaRight) {
    const radius = Math.round(footerSize / 4);
    const textWidth = measureTracked(ctx, data.metaRight, footerSpacing);
    const textX = CARD.w - CARD.pad - textWidth;
    ctx.fillStyle = data.accent;
    ctx.beginPath();
    ctx.arc(textX - 12 - radius, LAYOUT.footerBaseline - radius - 2, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = data.muted;
    drawTracked(ctx, data.metaRight, textX, LAYOUT.footerBaseline, footerSpacing);
  }

  if (data.domain) {
    ctx.font = `500 25px ${data.font}`;
    ctx.fillStyle = data.muted;
    const spacing = 6;
    const width = measureTracked(ctx, data.domain, spacing);
    drawTracked(ctx, data.domain, (CARD.w - width) / 2, LAYOUT.domainBaseline, spacing);
  }
}

function toFile(canvas, filename) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob ? new File([blob], filename, { type: 'image/png' }) : null);
    }, 'image/png');
  });
}

/* A galeria e um Splide; o slide visivel manda na foto do card. */
function currentGalleryImage(scope) {
  const root = scope || document;
  const active = root.querySelector('.main-carousel .splide__slide.is-active img');
  if (!active) return null;
  return active.currentSrc || active.src || null;
}

class ShareCard extends HTMLElement {
  constructor() {
    super();
    this.data = JSON.parse(this.querySelector('[data-share-card-data]').textContent);
    this.canvas = this.querySelector('[data-share-card-canvas]');
    this.shareButton = this.querySelector('[data-share-card-share]');
    this.downloadButton = this.querySelector('[data-share-card-download]');
    this.statusElement = this.querySelector('[data-share-card-status]');
    this.file = null;
    this.renderedPhoto = null;

    this.shareButton.addEventListener('click', () => this.share());
    this.downloadButton.addEventListener('click', () => this.download());
  }

  setStatus(message) {
    if (this.statusElement) this.statusElement.textContent = message || '';
  }

  async prepare(photoSrc) {
    const photo = photoSrc || this.data.photo;
    if (!photo) {
      this.setStatus(this.data.textError);
      return;
    }
    if (this.renderedPhoto === photo && this.file) return;

    this.setStatus(this.data.textBuilding);
    this.shareButton.hidden = true;
    this.downloadButton.disabled = true;
    this.classList.add('share-card--loading');

    try {
      await ensureFonts(this.data.font);
      const [photoImage, logoImage] = await Promise.all([
        loadImage(sizedUrl(photo, 1200)),
        this.data.logo ? loadImage(sizedUrl(this.data.logo, 480)).catch(() => null) : Promise.resolve(null),
      ]);

      drawCard(this.canvas.getContext('2d'), this.data, { photo: photoImage, logo: logoImage });
      this.file = await toFile(this.canvas, this.data.filename);
      this.renderedPhoto = photo;

      const canShareFile = Boolean(
        this.file && navigator.canShare && navigator.canShare({ files: [this.file] })
      );
      this.shareButton.hidden = !canShareFile;
      this.downloadButton.disabled = false;
      this.setStatus(canShareFile ? this.data.textHintShare : this.data.textHintDownload);
    } catch (error) {
      this.setStatus(this.data.textError);
      console.error('[share-card]', error);
    } finally {
      this.classList.remove('share-card--loading');
    }
  }

  async share() {
    if (!this.file) return;
    try {
      /* Só o arquivo: com url no mesmo payload, vários apps publicam o link e
       * descartam a imagem. */
      await navigator.share({ files: [this.file], title: this.data.shareTitle });
    } catch (error) {
      /* cancelado pelo usuario */
    }
  }

  download() {
    if (!this.file) return;
    const url = URL.createObjectURL(this.file);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.data.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

if (!customElements.get('share-card')) customElements.define('share-card', ShareCard);

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-share-card-open]');
  if (!opener) return;
  const modal = document.querySelector(opener.dataset.shareCardOpen);
  if (!modal) return;
  event.preventDefault();
  modal.show(opener);
  const card = modal.querySelector('share-card');
  /* Limita a busca da foto a secao do proprio botao: com um quick view aberto
   * sobre a pagina existe mais de uma galeria no documento. */
  if (card) card.prepare(currentGalleryImage(opener.closest('.shopify-section, .product')));
});
